import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Clock, MapPin, Users, Download, Edit, Trash2 } from 'lucide-react';

const formatDateForCal = (date) => {
  if (!date) return '';
  return new Date(date).toISOString().replace(/-|:|\.\d+/g, '');
};

const getGoogleCalendarUrl = (event) => {
  const start = formatDateForCal(event.start_time);
  const end = event.end_time ? formatDateForCal(event.end_time) : formatDateForCal(new Date(new Date(event.start_time).getTime() + 3600000));
  const params = new URLSearchParams({ action: 'TEMPLATE', text: event.title, dates: `${start}/${end}`, details: event.description || '', location: event.location || '' });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const getOutlookCalendarUrl = (event) => {
  const params = new URLSearchParams({
    path: '/calendar/action/compose', rru: 'addevent', subject: event.title,
    startdt: new Date(event.start_time).toISOString(),
    enddt: event.end_time ? new Date(event.end_time).toISOString() : new Date(new Date(event.start_time).getTime() + 3600000).toISOString(),
    body: event.description || '', location: event.location || ''
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

const downloadICSFile = (event) => {
  const start = formatDateForCal(event.start_time);
  const end = event.end_time ? formatDateForCal(event.end_time) : formatDateForCal(new Date(new Date(event.start_time).getTime() + 3600000));
  const ics = ['BEGIN:VCALENDAR','VERSION:2.0','BEGIN:VEVENT',`DTSTART:${start}`,`DTEND:${end}`,`SUMMARY:${event.title}`,`DESCRIPTION:${event.description || ''}`,`LOCATION:${event.location || ''}`,'END:VEVENT','END:VCALENDAR'].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const CalendarEventCard = ({ event, eventTypeInfo, onEdit, onDelete }) => (
  <Card className="surface-card" data-testid={`event-${event.id}`}>
    <CardContent className="pt-6">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 ${eventTypeInfo.color} rounded-full flex items-center justify-center text-white flex-shrink-0`}>
          {eventTypeInfo.icon}
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-foreground text-lg">{event.title}</h3>
              <p className="text-sm text-muted-foreground">{eventTypeInfo.label}</p>
            </div>
            <div className="flex gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid={`add-to-calendar-${event.id}`}>
                    <Download className="w-4 h-4 text-emerald-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => window.open(getGoogleCalendarUrl(event), '_blank')}>Google Calendar</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => window.open(getOutlookCalendarUrl(event), '_blank')}>Outlook Calendar</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadICSFile(event)}>Apple Calendar (Download)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="ghost" size="icon" onClick={() => onEdit(event)} data-testid={`edit-event-${event.id}`}>
                <Edit className="w-4 h-4 text-muted-foreground" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onDelete(event.id)} data-testid={`delete-event-${event.id}`}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          </div>

          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4 text-primary flex-shrink-0" />
              {event.all_day ? <span>All Day</span> : (
                <span>
                  {new Date(event.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  {event.end_time && ` - ${new Date(event.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                </span>
              )}
            </div>
            {event.location && (
              <div className="flex items-start gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <span>{event.location}</span>
              </div>
            )}
            {event.attendees && event.attendees.length > 0 && (
              <div className="flex items-start gap-2 text-muted-foreground">
                <Users className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <span>{event.attendees.length} attendee(s)</span>
              </div>
            )}
            {event.description && <p className="text-muted-foreground mt-2">{event.description}</p>}
            <p className="text-xs text-muted-foreground mt-2">Created by {event.creator?.name}</p>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default CalendarEventCard;
